import {
  DockableDrawer,
  type DockableDrawerProps,
} from '@ctw/shared/components/containers/dockable-drawer';
import { Drawer, type DrawerProps } from '@ctw/shared/components/containers/drawer';
import { ErrorBoundary } from '@ctw/shared/components/errors/error-boundary';
import {
  type TelemetryContext,
  useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { SetOptional } from 'type-fest';

export interface OpenDrawerAction
  extends Pick<DrawerProps, 'label' | 'title' | 'subtitle' | 'badges' | 'content'> {
  allowDocking?: boolean;
  telemetryContext?: TelemetryContext;
  onDrawerEvent?: (eventType: DrawerEventType) => void;
}

type DrawerEventType = 'open' | 'afterOpen' | 'close' | 'afterClose';

type DrawerEventHandler = (eventType: DrawerEventType) => void;

export interface DrawerBoundary {
  name: string;
  telemetry: ReturnType<typeof useTelemetry>;
  context?: TelemetryContext;
}

interface OpenDrawerArgs {
  telemetry: ReturnType<typeof useTelemetry>;
  action: OpenDrawerAction;
}

export interface DrawerState {
  isDrawerOpen: boolean;
  isDrawerDocked: boolean;
  isDrawerTransitioning: boolean;
  openDrawer: (args: OpenDrawerArgs) => void;
  closeDrawer: () => void;
  layout: DrawerLayout;
  setLayout: (layout: DrawerLayout) => void;
  drawerBoundary: DrawerBoundary;
  setDrawerBoundary: (boundary: DrawerBoundary) => void;
  onDrawerEvent: (boundaryName: string, eventHandler: DrawerEventHandler) => () => void;
}

const emptyDrawerAction: OpenDrawerAction = {
  title: '',
  badges: [],
  allowDocking: true,
  content: {
    body: null,
    footer: null,
  },
} as const;

const defaultDrawerBoundary: SetOptional<DrawerBoundary, 'telemetry'> = {
  name: 'Drawer',
} as const;

const DrawerContext = createContext<DrawerState | null>(null);

interface DrawerProviderChildrenArgs {
  drawer: ReactNode;
  setLayout: (layout: DrawerLayout) => void;
  isDrawerOpen: boolean;
}

interface DrawerProviderProps {
  children: (args: DrawerProviderChildrenArgs) => ReactNode;
  allowDocking: boolean;
  dockingMode?: DockableDrawerProps['dockingMode'];
  dockDisplay?: DockableDrawerProps['dockDisplay'];
}

export const DrawerProvider = ({
  children,
  allowDocking,
  dockingMode,
  dockDisplay,
}: DrawerProviderProps) => {
  const providerTelemetry = useTelemetry();
  const [openDrawerAction, setOpenDrawerAction] = useState<OpenDrawerAction>(emptyDrawerAction);
  const [layout, setLayout] = useState<DrawerLayout>(defaultDrawerLayout);
  const [drawerBoundary, setDrawerBoundary] = useState<DrawerBoundary>({
    ...defaultDrawerBoundary,
    telemetry: providerTelemetry,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isDrawerTransitioning = useRef(false);

  const drawerIsDockable = useMemo(
    () => Boolean(allowDocking && openDrawerAction.allowDocking),
    [allowDocking, openDrawerAction.allowDocking],
  );

  const eventRegistrations: Record<string, Array<DrawerEventHandler>> = useMemo(() => ({}), []);

  const openDrawer: DrawerState['openDrawer'] = useCallback(
    ({ telemetry, action }) => {
      if (!(isDrawerTransitioning.current || isDrawerTransitioning.current)) {
        telemetry.trackInteraction('drawer_open', {
          ...drawerBoundary.context,
          drawerName: drawerBoundary.name,
          withinDrawer: true,
          ...action.telemetryContext,
        });
        setIsDrawerOpen(true);
        setOpenDrawerAction(action);
        setDrawerBoundary((previous) => ({ ...previous, telemetry }));
      }
    },
    [drawerBoundary],
  );

  const closeDrawer: DrawerState['closeDrawer'] = useCallback(() => {
    if (!(isDrawerTransitioning.current || isDrawerTransitioning.current)) {
      handleEvent('close');
      drawerBoundary.telemetry.trackInteraction('drawer_close', {
        ...drawerBoundary.context,
        drawerName: drawerBoundary.name,
        withinDrawer: true,
        ...openDrawerAction.telemetryContext,
      });
      setIsDrawerOpen(false);
      setTimeout(() => {
        setOpenDrawerAction(emptyDrawerAction);
        setDrawerBoundary({ ...defaultDrawerBoundary, telemetry: providerTelemetry });
        setLayout(defaultDrawerLayout);
      }, 300);
    }
  }, [providerTelemetry, drawerBoundary, openDrawerAction]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const state: DrawerState = useMemo(
    () => ({
      openDrawer,
      closeDrawer,
      isDrawerOpen,
      isDrawerDocked: drawerIsDockable && isDrawerOpen,
      isDrawerTransitioning: isDrawerTransitioning.current,
      drawerBoundary,
      setDrawerBoundary,
      layout,
      setLayout,
      onDrawerEvent: (boundaryName, eventHandler) => {
        if (!Array.isArray(eventRegistrations[boundaryName])) {
          eventRegistrations[boundaryName] = [];
        }

        eventRegistrations[boundaryName].push(eventHandler);

        return () => {
          eventRegistrations[boundaryName] = eventRegistrations[boundaryName].filter(
            (handler) => handler !== eventHandler,
          );
        };
      },
    }),
    [drawerBoundary, closeDrawer, isDrawerOpen, openDrawer],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  const handleEvent = useCallback(
    (eventType: DrawerEventType) => {
      switch (eventType) {
        case 'open':
        case 'close':
          isDrawerTransitioning.current = true;
          break;
        case 'afterOpen':
        case 'afterClose':
          isDrawerTransitioning.current = false;
          break;
        default:
          break;
      }

      openDrawerAction.onDrawerEvent?.(eventType);

      if (Array.isArray(eventRegistrations[drawerBoundary.name])) {
        for (const eventHandler of eventRegistrations[drawerBoundary.name]) {
          const invokeEventHandler = async () => eventHandler(eventType);
          void invokeEventHandler();
        }
      }
    },
    [drawerBoundary, openDrawerAction],
  );

  const drawer = useMemo(
    () => (
      <ErrorBoundary boundaryName={drawerBoundary.name} initialContext={drawerBoundary.context}>
        {() => (
          <>
            {drawerIsDockable && (
              <DockableDrawer
                layout={layout}
                dockingMode={dockingMode}
                dockDisplay={dockDisplay}
                isOpen={isDrawerOpen}
                onOpen={() => handleEvent('open')}
                onAfterOpen={() => handleEvent('afterOpen')}
                close={closeDrawer}
                onAfterClose={() => handleEvent('afterClose')}
                {...openDrawerAction}
              />
            )}
            {!drawerIsDockable && (
              <Drawer
                layout={layout}
                isOpen={isDrawerOpen}
                onOpen={() => handleEvent('open')}
                onAfterOpen={() => handleEvent('afterOpen')}
                close={closeDrawer}
                onAfterClose={() => handleEvent('afterClose')}
                {...openDrawerAction}
              />
            )}
          </>
        )}
      </ErrorBoundary>
    ),
    [
      drawerBoundary,
      closeDrawer,
      drawerIsDockable,
      handleEvent,
      isDrawerOpen,
      openDrawerAction,
      layout,
      dockDisplay,
      dockingMode,
    ],
  );

  return (
    <DrawerContext.Provider value={state}>
      {children({
        drawer,
        setLayout,
        isDrawerOpen,
      })}
    </DrawerContext.Provider>
  );
};

export interface DrawerLayout {
  showCloseButton: boolean;
  showDeepLinkCopyButton?: boolean;
}

const defaultDrawerLayout: DrawerLayout = {
  showCloseButton: true,
} as const;

export interface UseDrawerOptions {
  boundaryName: string;
  telemetryContext?: TelemetryContext;
  allowDocking?: boolean;
  onDrawerEvent?: (eventType: DrawerEventType) => void;
  layout?: DrawerLayout;
}

export const useDrawer = ({
  boundaryName,
  telemetryContext,
  allowDocking = true,
  onDrawerEvent,
  layout = defaultDrawerLayout,
}: UseDrawerOptions) => {
  const hookTelemetry = useTelemetry();
  const drawerContext = useContext(DrawerContext);

  const drawer: DrawerState | null = useMemo(() => {
    if (!drawerContext) {
      return null;
    }

    return {
      ...drawerContext,
      openDrawer: ({ telemetry, action }) => {
        drawerContext.setDrawerBoundary({
          name: boundaryName,
          context: {
            ...hookTelemetry.getScopedContextProperties(),
            ...telemetryContext,
            drawerName: boundaryName,
            withinDrawer: true,
            ...action.telemetryContext,
          },
          telemetry,
        });

        if (layout) {
          drawerContext.setLayout(layout);
        }

        drawerContext.openDrawer({
          telemetry,
          action: {
            ...action,
            allowDocking,
            telemetryContext: {
              ...hookTelemetry.getScopedContextProperties(),
              ...telemetryContext,
              drawerName: boundaryName,
              withinDrawer: true,
              ...action.telemetryContext,
            },
          },
        });
      },
    };
  }, [hookTelemetry, boundaryName, telemetryContext, allowDocking, drawerContext, layout]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(
    () =>
      drawer && onDrawerEvent
        ? drawer.onDrawerEvent(boundaryName, onDrawerEvent)
        : () => {
            /* noop */
          },
    [boundaryName, onDrawerEvent],
  );

  if (!drawer) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }

  return drawer;
};

interface PublicDrawerState
  extends Pick<
    DrawerState,
    'isDrawerOpen' | 'isDrawerDocked' | 'isDrawerTransitioning' | 'layout' | 'setLayout'
  > {
  boundary: DrawerBoundary;
}

export const useDrawerState = (): PublicDrawerState => {
  const telemetry = useTelemetry();
  const drawerState = useContext(DrawerContext);

  return {
    isDrawerOpen: drawerState?.isDrawerOpen ?? false,
    isDrawerDocked: drawerState?.isDrawerDocked ?? false,
    isDrawerTransitioning: drawerState?.isDrawerTransitioning ?? false,
    boundary: drawerState?.drawerBoundary ?? { ...defaultDrawerBoundary, telemetry },
    layout: drawerState?.layout ?? defaultDrawerLayout,
    // biome-ignore lint/suspicious/noEmptyBlockStatements: Intentional no-op
    setLayout: drawerState?.setLayout ?? (() => {}),
  };
};
